import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { supabase } from "../config/supabaseClient";

export default function JoinRequestModal({ show, onClose, group }) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('User error:', userError);
        throw userError;
      }

      if (!user) {
        setError('You must be logged in to submit a join request');
        return;
      }

      // Log the current user and group details
      console.log('Current user:', {
        id: user.id,
        email: user.email
      });
      
      console.log('Group details:', {
        id: group.id,
        name: group.name,
        visibility: group.visibility,
        admin_id: group.admin_id
      });

      // First check if user is already a member
      console.log('Checking membership...');
      const { data: existingMember, error: memberError } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', group.id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (memberError) {
        console.error('Error checking membership:', memberError);
        throw memberError;
      }

      console.log('Membership check result:', existingMember);

      if (existingMember) {
        setError('You are already a member of this group');
        return;
      }

      // Then check for existing pending requests
      console.log('Checking existing requests...');
      const { data: existingRequest, error: requestCheckError } = await supabase
        .from('join_requests')
        .select('id, status')
        .eq('group_id', group.id)
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (requestCheckError) {
        console.error('Error checking existing requests:', requestCheckError);
        throw requestCheckError;
      }

      console.log('Existing request check result:', existingRequest);

      if (existingRequest) {
        setError('You already have a pending request for this group');
        return;
      }

      console.log('Submitting join request:', {
        group_id: group.id,
        user_id: user.id,
        message: message,
        group_name: group.name
      });

      // Try to get the group first to verify it exists
      const { data: groupCheck, error: groupError } = await supabase
        .from('groups')
        .select('id, visibility, admin_id')
        .eq('id', group.id)
        .maybeSingle();

      console.log('Group check query result:', { groupCheck, groupError });

      if (groupError) {
        console.error('Error verifying group:', groupError);
        setError('Could not verify group exists');
        return;
      }

      if (!groupCheck) {
        console.error('Group not found:', group.id);
        setError('This group no longer exists');
        return;
      }

      // Verify user is not the admin
      if (groupCheck.admin_id === user.id) {
        setError('You cannot request to join a group you own');
        return;
      }

      // Submit join request
      try {
        const { data: joinRequest, error: insertError } = await supabase
          .from('join_requests')
          .insert({
            group_id: group.id,
            user_id: user.id,
            message: message,
            status: 'pending'
          })
          .select()
          .single();

        if (insertError) {
          console.error('Join request error:', {
            error: insertError,
            code: insertError.code,
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint,
            group_id: group.id,
            user_id: user.id,
            group_name: group.name
          });

          // Check specific error cases
          if (insertError.code === '42501') { // Permission denied
            setError('Permission denied. Please try logging out and back in.');
          } else if (insertError.code === '23505') { // Unique violation
            setError('You already have a request for this group');
          } else if (insertError.code === '23503') { // Foreign key violation
            setError('Invalid group or user ID');
          } else {
            setError('Failed to submit join request. Please try again later.');
          }
          return;
        }

        console.log('Join request submitted successfully:', joinRequest);
        alert('Join request sent successfully!');
        onClose();
        navigate('/');
      } catch (error) {
        console.error('Unexpected error submitting request:', error);
        setError('An unexpected error occurred. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting join request:', error);
      setError(error.message || 'Failed to submit join request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
        <h3 className="text-xl font-bold text-center mb-4">Request to Join {group.name}</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-semibold mb-2" htmlFor="message">
              Message to Group Admin
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Introduce yourself and explain why you'd like to join this group..."
              className="w-full border border-gray-300 rounded-lg p-3 h-32 resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              required
            />
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-400 rounded-lg hover:bg-gray-100"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

JoinRequestModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired
}; 